import { useState } from "react";
import { useTranslation } from "react-i18next";
import { computeActualSeconds } from "@/lib/offline";
import { fmtClock, fmtMins, fmtDur, STATUS_DESC } from "@/lib/format";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Play,
  Square,
  Coffee,
  Undo2,
  RotateCcw,
  CheckCircle2,
  Check,
  Star,
  HandHelping,
  Clock,
  Sparkles,
  TriangleAlert,
  Wrench,
  BellOff,
  KeyRound,
  Users,
} from "lucide-react";

export default function RoomCard({ clean, onAct, onReport, onToggle, onAssist, onRelease, onUndoStart, onContinue, onCompleteAllEnd, onCompleteAllEndAssist, onStrip, onKeyReturn, onDnd, onUndoDnd, isManager, meId, focused = false }) {
  const { t } = useTranslation();
  const myRole = clean.my_role || "primary";
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [ackKind, setAckKind] = useState("primary");
  const isAssistRole = myRole === "assisting" || myRole === "assist_available";
  const myAssist = (clean.assist_sessions || []).find((a) => a.cleaner_id === meId);
  const isDnd = !!clean.dnd;

  // the session that THIS user drives (their own primary or assist session)
  const drive = isAssistRole ? myAssist || { status: "pending" } : clean.session || { status: "pending" };
  const status = drive.status || "pending";
  const completed = status === "completed";
  const running = status === "in_progress";
  const paused = status === "paused";
  const live = drive.started_at ? computeActualSeconds(drive) : 0;

  const isExtra = clean.is_extra;
  const isTask = clean.is_general;
  const special = isExtra || isTask;
  const issues = clean.issues || [];
  const checklist = clean.checklist || [];
  const doneCount = checklist.filter((i) => i.checked).length;
  const allDone = checklist.length > 0 && doneCount === checklist.length;
  const checklistLocked = myRole === "assist_available" || !(running || paused);
  // only show sub-tasks while the clean is actually underway, or when the cleaner
  // left partial work behind (some real tasks ticked but the room isn't finished).
  // Hidden before the clean starts and once the room is completed.
  const partialLeft = checklist.some((i) => i.checked && !i.is_strip);
  const showChecklist = running || paused || (!completed && partialLeft);
  const needsStrip = !special && clean.clean_type === "full";
  const stripped = !!(clean.strip && clean.strip.done);
  const keyReturned = !!clean.key_returned;
  // Guest count for linen/towels. Prefer the STAYING guest(s) (not the departing
  // checkout) so a turnover room shows the incoming booking's pax.
  const paxNum = (s) => { const m = String(s ?? "").match(/\d+/); return m ? parseInt(m[0], 10) : 0; };
  const allGuests = clean.guests || [];
  const stayingGuests = allGuests.filter((g) => (g.status || "").toLowerCase() !== "departing");
  const paxGuests = stayingGuests.length ? stayingGuests : allGuests;
  const pax = paxGuests.reduce((a, g) => ({ adults: a.adults + paxNum(g.adults), children: a.children + paxNum(g.children) }), { adults: 0, children: 0 });
  // A helper can only assist once a primary has actually STARTED the room (running or on
  // a break). Mirrors the backend guard so the Assist button can't even be tapped early.
  const primaryRunning = !!clean.claimed_by && (clean.session?.status === "in_progress" || clean.session?.status === "paused");
  const assistBlocked = !isManager && !primaryRunning;
  const specialItems = checklist.filter((i) => i.special);
  const ringCls = special ? "" : clean.is_priority ? "ring-priority" : `ring-${clean.status}`;

  const handleEndClean = () => {
    if (checklist.length > 0 && !allDone) {
      setConfirmEnd(true);
    } else {
      onAct(clean, "end_clean");
    }
  };

  const handleEndAssist = () => {
    if (isManager && checklist.length > 0 && !allDone) {
      setConfirmEnd(true);
    } else {
      onAssist(clean, "end");
    }
  };

  const startPrimary = () => {
    if (specialItems.length > 0) { setAckKind("primary"); setAckOpen(true); }
    else onAct(clean, "start_clean");
  };
  const startAssist = () => {
    if (specialItems.length > 0) { setAckKind("assist"); setAckOpen(true); }
    else onAssist(clean, "start");
  };
  const confirmAck = () => {
    setAckOpen(false);
    if (ackKind === "primary") onAct(clean, "start_clean");
    else onAssist(clean, "start");
  };

  return (
    <div
      id={`rc-${clean.id}`}
      data-testid={`room-card-${clean.room_number}`}
      data-kind={special ? "other" : clean.clean_type === "service" ? "service" : "clean"}
      className={`k-fade-in relative overflow-hidden rounded-xl border border-l-[6px] bg-card p-5 shadow-sm ${
        special ? "border-l-purple-500" : clean.clean_type === "service" ? "border-l-teal-500" : "border-l-emerald-600"
      } ${ringCls} ${focused ? "ring-2 ring-amber-400 shadow-md" : ""} ${
        clean.is_priority && !completed && !special ? "priority-pulse" : ""
      } ${completed ? "opacity-70" : ""} ${isExtra ? "border-purple-200" : ""} ${isTask ? "border-blue-200" : ""} ${
        needsStrip && stripped && !completed ? "card-stripped" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-3xl font-extrabold">
              {special ? clean.room_number : `${t("Room")} ${clean.room_number}`}
            </span>
          </div>
          <div className="mt-0.5 text-sm text-muted-foreground">{clean.room_type}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {isExtra ? (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-purple-200 bg-purple-100 px-3 py-1.5 text-sm font-bold text-purple-700">
              <Sparkles className="h-4 w-4" /> {t("Extra")}
            </span>
          ) : isTask ? (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-100 px-3 py-1.5 text-sm font-bold text-blue-700">
              <Sparkles className="h-4 w-4" /> {t("Task")}
            </span>
          ) : clean.is_priority ? (
            <PriorityBadge size="lg" />
          ) : (
            <StatusBadge status={clean.status} size="lg" />
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <Clock className="h-4 w-4 text-muted-foreground" />
          {t("Est")} {fmtMins(clean.est_minutes)}
        </span>
        {!special && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{t(STATUS_DESC[clean.status])}</span>
          </>
        )}
        {!special && (pax.adults > 0 || pax.children > 0) && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="inline-flex items-center gap-1.5 font-medium" data-testid={`pax-${clean.room_number}`}>
              <Users className="h-4 w-4 text-muted-foreground" />
              {pax.adults} {pax.adults === 1 ? t("adult") : t("adults")}
              {pax.children > 0 ? ` · ${pax.children} ${pax.children === 1 ? t("child") : t("children")}` : ""}
            </span>
          </>
        )}
      </div>

      {/* role / helpers banner */}
      {isAssistRole && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700" data-testid={`assist-banner-${clean.room_number}`}>
          <HandHelping className="h-4 w-4" />
          {isManager
            ? (clean.primary_name ? t("Helping {{name}} · record only", { name: clean.primary_name }) : t("Manager clean · record only"))
            : myRole === "assist_available" ? t("Primary: {{name}} — you can help", { name: clean.primary_name || t("teammate") }) : t("Assisting {{name}}", { name: clean.primary_name || t("teammate") })}
        </div>
      )}
      {myRole === "primary" && (clean.assist_names || []).length > 0 && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700">
          <HandHelping className="h-4 w-4" /> {t("Helped by {{names}}", { names: clean.assist_names.join(", ") })}
        </div>
      )}

      {/* live timer */}
      {(running || paused || completed) && !isDnd && (
        <div className="mt-4 flex items-center justify-between rounded-lg bg-secondary/60 px-4 py-3">
          <span className="label-caps text-muted-foreground">
            {completed
              ? isAssistRole ? (isManager ? t("Your clean time") : t("Your assist time")) : t("Actual time")
              : paused ? t("On break") : isAssistRole ? (isManager ? t("Cleaning") : t("Assisting")) : t("Cleaning")}
          </span>
          <span
            data-testid={`timer-${clean.room_number}`}
            className={`font-display text-2xl font-extrabold tabular-nums ${
              paused ? "text-amber-600" : completed ? "text-emerald-600" : "text-blue-600"
            }`}
          >
            {completed ? fmtDur(live) : fmtClock(live)}
          </span>
        </div>
      )}

      {/* sub-task checklist for the day */}
      {checklist.length > 0 && showChecklist && (
        <div className="mt-4 rounded-lg border border-border bg-secondary/30 p-3" data-testid={`checklist-${clean.room_number}`}>
          <div className="mb-2 flex items-center justify-between">
            <span className="label-caps text-muted-foreground">{t("Today's tasks")}</span>
            <span className={`text-xs font-bold ${allDone ? "text-emerald-600" : "text-muted-foreground"}`}>
              {doneCount}/{checklist.length} {t("done")}
            </span>
          </div>
          <div className="space-y-1">
            {checklist.map((it) => (
              <button
                key={it.id}
                type="button"
                disabled={checklistLocked}
                onClick={() => onToggle(clean, it.id)}
                data-testid={`checklist-item-${clean.room_number}-${it.id}`}
                className={`flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm ${
                  it.special ? "bg-amber-50 ring-1 ring-amber-200" : ""
                } ${checklistLocked ? "cursor-default" : "hover:bg-secondary"}`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${
                    it.checked
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : it.special ? "border-amber-400" : "border-muted-foreground/40"
                  }`}
                >
                  {it.checked && <Check className="h-3.5 w-3.5" />}
                </span>
                <span className="flex-1">
                  {it.special && <Star className="mr-1 inline h-3.5 w-3.5 -translate-y-px fill-amber-400 text-amber-500" />}
                  {it.section && !it.special && <span className="text-xs text-muted-foreground">{it.section} · </span>}
                  <span className={it.checked ? "text-muted-foreground line-through" : `font-medium ${it.special ? "text-amber-800" : ""}`}>{it.name}</span>
                  {it.special && <span className="ml-1 text-[11px] font-bold uppercase text-amber-600">{t("Special")}</span>}
                  {it.checked && it.checked_by_name && it.checked_by !== clean.claimed_by && (
                    <span className="ml-1 text-[11px] font-semibold text-indigo-600">· {it.checked_by_name}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
          {allDone && !checklistLocked && (
            <div className="mt-2 flex items-center gap-1.5 rounded-md bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> {t("All tasks ticked — ready to")} {isAssistRole ? (isManager ? t("End Clean") : t("End Assist")) : t("End Clean")}
            </div>
          )}
        </div>
      )}

      {/* reported issues */}
      {issues.length > 0 && (
        <div
          className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
          data-testid={`issues-${clean.room_number}`}
        >
          <TriangleAlert className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="text-xs font-semibold text-amber-800">
            {t("{{count}} problem(s) reported:", { count: issues.length })}
          </span>
          {issues.map((i) => (
            <span key={i.id} className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-amber-800">
              {i.category}
            </span>
          ))}
        </div>
      )}

      {/* late checkout — don't start stripping until the (later) checkout time */}
      {needsStrip && clean.late_checkout && !keyReturned && !stripped && status === "pending" && !completed && (
        <div
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700"
          data-testid={`late-checkout-${clean.room_number}`}
        >
          <Clock className="h-3.5 w-3.5" /> {t("Late checkout {{time}} — don't strip yet", { time: clean.checkout_time })}
        </div>
      )}

      {/* stripping — guest must leave (key returned) then room stripped before work starts */}
      {needsStrip && status === "pending" && !completed && (
        stripped ? (
          <div
            className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"
            data-testid={`strip-status-${clean.room_number}`}
          >
            <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> {t("Stripped by {{name}}", { name: clean.strip?.by_name || "—" })}
              {clean.strip?.est_added && (
                <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700">{t("+5 min")}</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => onStrip(clean, false)}
              data-testid={`undo-strip-${clean.room_number}`}
              className="shrink-0 text-xs font-semibold text-muted-foreground underline hover:text-foreground"
            >
              {t("Undo")}
            </button>
          </div>
        ) : keyReturned ? (
          <div
            className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2"
            data-testid={`key-returned-status-${clean.room_number}`}
          >
            <span className="flex items-center gap-1.5 text-xs font-semibold text-sky-700">
              <KeyRound className="h-4 w-4" /> {clean.key_return?.by_name ? t("Key returned by {{name}} — ready to strip", { name: clean.key_return.by_name }) : t("Key returned — ready to strip")}
            </span>
            <button
              type="button"
              onClick={() => onKeyReturn(clean, false)}
              data-testid={`undo-key-return-${clean.room_number}`}
              className="shrink-0 text-xs font-semibold text-muted-foreground underline hover:text-foreground"
            >
              {t("Undo")}
            </button>
          </div>
        ) : (
          <div
            className="mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800"
            data-testid={`key-return-required-${clean.room_number}`}
          >
            <TriangleAlert className="h-4 w-4 shrink-0 text-amber-600" /> {t("Guest hasn't left yet — mark the key returned before stripping")}
          </div>
        )
      )}

      {/* actions */}
      <div className="mt-4 space-y-3">
        {isDnd ? (
          <>
            <div
              className="flex items-center justify-center gap-2 rounded-lg bg-slate-100 py-3 text-base font-bold text-slate-600"
              data-testid={`dnd-status-${clean.room_number}`}
            >
              <BellOff className="h-5 w-5" /> {t("Do Not Disturb — closed, no time")}
            </div>
            <Button
              variant="outline"
              data-testid={`undo-dnd-${clean.room_number}`}
              onClick={() => onUndoDnd(clean)}
              className="k-press h-11 w-full gap-2 text-sm font-semibold"
            >
              <RotateCcw className="h-4 w-4" /> {t("Undo — put back on the board")}
            </Button>
          </>
        ) : myRole === "primary" ? (
          <>
            {status === "pending" && (
              needsStrip && !stripped ? (
                keyReturned ? (
                  <Button
                    data-testid={`mark-stripped-${clean.room_number}`}
                    onClick={() => onStrip(clean, true)}
                    className="k-press h-14 w-full gap-2 bg-amber-600 text-lg font-bold hover:bg-amber-700"
                  >
                    <Sparkles className="h-5 w-5" /> {t("Mark stripped")}
                  </Button>
                ) : (
                  <Button
                    data-testid={`key-returned-${clean.room_number}`}
                    onClick={() => onKeyReturn(clean, true)}
                    className="k-press h-14 w-full gap-2 bg-sky-600 text-lg font-bold hover:bg-sky-700"
                  >
                    <KeyRound className="h-5 w-5" /> {t("Key Returned/Departed")}
                  </Button>
                )
              ) : (
                <Button
                  data-testid={`start-clean-${clean.room_number}`}
                  onClick={startPrimary}
                  data-kind={special ? "other" : clean.clean_type === "service" ? "service" : "clean"}
                  className={`k-press h-14 w-full gap-2 text-lg font-bold ${
                    special
                      ? "bg-purple-600 text-white hover:bg-purple-700"
                      : clean.clean_type === "service"
                        ? "bg-teal-600 text-white hover:bg-teal-700"
                        : "bg-emerald-700 text-white hover:bg-emerald-800"
                  }`}
                >
                  <Play className="h-5 w-5" /> {special ? t("Start Task") : clean.clean_type === "service" ? t("Start Service") : t("Start Clean")}
                </Button>
              )
            )}
            {status === "pending" && !special && clean.clean_type === "service" && (
              <Button
                variant="outline"
                data-testid={`dnd-${clean.room_number}`}
                onClick={() => onDnd(clean)}
                className="k-press h-11 w-full gap-2 border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                <BellOff className="h-4 w-4" /> {t("Do Not Disturb — close, no time")}
              </Button>
            )}
            {running && (
              <Button
                data-testid={`end-clean-${clean.room_number}`}
                onClick={handleEndClean}
                className="k-press h-14 w-full gap-2 bg-emerald-600 text-base font-bold hover:bg-emerald-700"
              >
                <Square className="h-5 w-5" /> {t("End Clean")}
              </Button>
            )}
            {paused && (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 rounded-lg bg-amber-50 py-2.5 text-sm font-bold text-amber-700" data-testid={`on-break-${clean.room_number}`}>
                  <Coffee className="h-4 w-4" /> {t("On break — resume from the Break panel above")}
                </div>
                <Button
                  data-testid={`end-clean-${clean.room_number}`}
                  onClick={handleEndClean}
                  className="k-press h-14 w-full gap-2 bg-emerald-600 text-base font-bold hover:bg-emerald-700"
                >
                  <Square className="h-5 w-5" /> {t("End Clean")}
                </Button>
              </div>
            )}
            {(running || paused) && (
              <Button
                variant="ghost"
                data-testid={`undo-start-${clean.room_number}`}
                onClick={() => onUndoStart(clean)}
                className="k-press h-9 w-full gap-2 text-xs font-semibold text-muted-foreground hover:bg-secondary"
              >
                <Undo2 className="h-3.5 w-3.5" /> {t("Undo start (wrong room)")}
              </Button>
            )}
            {completed && (
              <>
                <div
                  className="flex items-center justify-center gap-2 rounded-lg bg-emerald-50 py-3 text-base font-bold text-emerald-700"
                  data-testid={`completed-${clean.room_number}`}
                >
                  <CheckCircle2 className="h-5 w-5" /> {t("Completed")}
                </div>
                <Button
                  variant="outline"
                  data-testid={`continue-clean-${clean.room_number}`}
                  onClick={() => onContinue(clean)}
                  className="k-press h-11 w-full gap-2 text-sm font-semibold"
                >
                  <RotateCcw className="h-4 w-4" /> {t("Continue cleaning")}
                </Button>
              </>
            )}
          </>
        ) : (
          <>
            {(myRole === "assist_available" || status === "pending" || completed) && (
              needsStrip && !stripped && !completed ? (
                keyReturned ? (
                  <Button
                    data-testid={`mark-stripped-${clean.room_number}`}
                    onClick={() => onStrip(clean, true)}
                    className="k-press h-14 w-full gap-2 bg-amber-600 text-lg font-bold hover:bg-amber-700"
                  >
                    <Sparkles className="h-5 w-5" /> {t("Mark stripped")}
                  </Button>
                ) : (
                  <Button
                    data-testid={`key-returned-${clean.room_number}`}
                    onClick={() => onKeyReturn(clean, true)}
                    className="k-press h-14 w-full gap-2 bg-sky-600 text-lg font-bold hover:bg-sky-700"
                  >
                    <KeyRound className="h-5 w-5" /> {t("Key Returned/Departed")}
                  </Button>
                )
              ) : (
                <>
                <Button
                  data-testid={`assist-start-${clean.room_number}`}
                  onClick={startAssist}
                  disabled={assistBlocked}
                  className={`k-press h-14 w-full gap-2 text-lg font-bold ${isManager ? "" : "bg-indigo-600 hover:bg-indigo-700"} ${assistBlocked ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isManager ? <Play className="h-5 w-5" /> : <HandHelping className="h-5 w-5" />}
                  {isManager ? (completed ? t("Clean again") : t("Start Clean")) : (completed ? t("Assist again") : t("Assist"))}
                </Button>
                {assistBlocked && (
                  <p className="mt-1.5 text-center text-xs font-medium text-muted-foreground" data-testid={`assist-blocked-hint-${clean.room_number}`}>
                    {t("Waiting for a cleaner to start this room before you can help.")}
                  </p>
                )}
                </>
              )
            )}
            {running && (
              <Button
                data-testid={`assist-end-${clean.room_number}`}
                onClick={handleEndAssist}
                className="k-press h-14 w-full gap-2 bg-emerald-600 text-base font-bold hover:bg-emerald-700"
              >
                <Square className="h-5 w-5" /> {isManager ? t("End Clean") : t("End Assist")}
              </Button>
            )}
            {paused && (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 rounded-lg bg-amber-50 py-2.5 text-sm font-bold text-amber-700" data-testid={`assist-on-break-${clean.room_number}`}>
                  <Coffee className="h-4 w-4" /> {t("On break — resume from the Break panel above")}
                </div>
                <Button
                  data-testid={`assist-end-${clean.room_number}`}
                  onClick={handleEndAssist}
                  className="k-press h-14 w-full gap-2 bg-emerald-600 text-base font-bold hover:bg-emerald-700"
                >
                  <Square className="h-5 w-5" /> {isManager ? t("End Clean") : t("End Assist")}
                </Button>
              </div>
            )}
          </>
        )}

        {!completed && myRole !== "assist_available" && (
          <Button
            variant="ghost"
            onClick={onReport}
            data-testid={`report-problem-${clean.room_number}`}
            className="k-press h-10 w-full gap-2 text-sm font-semibold text-amber-700 hover:bg-amber-50"
          >
            <Wrench className="h-4 w-4" /> {t("Report a problem")}
          </Button>
        )}
      </div>

      <Dialog open={confirmEnd} onOpenChange={setConfirmEnd}>
        <DialogContent data-testid={`end-confirm-dialog-${clean.room_number}`}>
          <DialogHeader>
            <DialogTitle>{t("Finish Room {{n}}?", { n: clean.room_number })}</DialogTitle>
            <DialogDescription>
              {t("Only {{done}} of {{total}} tasks are ticked. What would you like to do?", { done: doneCount, total: checklist.length })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              data-testid={`end-confirm-complete-${clean.room_number}`}
              onClick={() => { setConfirmEnd(false); (isManager ? onCompleteAllEndAssist : onCompleteAllEnd)(clean); }}
              className="k-press h-12 w-full gap-2 bg-emerald-600 text-base font-bold hover:bg-emerald-700"
            >
              <CheckCircle2 className="h-5 w-5" /> {t("Yes, all complete")}
            </Button>
            <Button
              variant="outline"
              data-testid={`end-confirm-partial-${clean.room_number}`}
              onClick={() => { setConfirmEnd(false); isManager ? onAssist(clean, "end") : onRelease(clean); }}
              className="k-press h-12 w-full gap-2 border-amber-300 text-base font-bold text-amber-700 hover:bg-amber-50"
            >
              <HandHelping className="h-5 w-5" /> {t("End now — leave the rest for another cleaner")}
            </Button>
            <Button
              variant="ghost"
              data-testid={`end-confirm-cancel-${clean.room_number}`}
              onClick={() => setConfirmEnd(false)}
              className="k-press h-10 w-full text-sm font-semibold text-muted-foreground"
            >
              {t("Keep cleaning")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ackOpen} onOpenChange={setAckOpen}>
        <DialogContent data-testid={`special-ack-dialog-${clean.room_number}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-amber-400 text-amber-500" /> {t("Special tasks for Room {{n}}", { n: clean.room_number })}
            </DialogTitle>
            <DialogDescription>
              {t("Please read these before you start — they need extra attention on this room.")}
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2" data-testid={`special-ack-list-${clean.room_number}`}>
            {specialItems.map((it) => (
              <li
                key={it.id}
                data-testid={`special-ack-item-${it.id}`}
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800"
              >
                <Star className="mt-0.5 h-4 w-4 shrink-0 fill-amber-400 text-amber-500" />
                <span>{it.name}</span>
              </li>
            ))}
          </ul>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              data-testid={`special-ack-confirm-${clean.room_number}`}
              onClick={confirmAck}
              className="k-press h-12 w-full gap-2 text-base font-bold"
            >
              <Check className="h-5 w-5" /> {t("Got it — start")}
            </Button>
            <Button
              variant="ghost"
              data-testid={`special-ack-cancel-${clean.room_number}`}
              onClick={() => setAckOpen(false)}
              className="k-press h-10 w-full text-sm font-semibold text-muted-foreground"
            >
              {t("Cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
